import { useActingRole, type ActingRole } from '@brush/auth';
import type { RoleChoice } from '@brush/ui';
import { MoreHorizontal } from 'lucide-react';
import type { Room } from '@brush/api-client';
import { About } from './About';
import { RailPanel, panelLabel } from './RailPanel';
import { shortLabelFor } from '../roles';

/**
 * The room, the role and the account, below 2xl (1536px).
 *
 * Below `desktop:` the header has room for the brand block, the current section's name and two 44px
 * controls, and very little else: the room switcher, the role switcher, the signed-in name and
 * Sign out together ask for more width than there is. So they collapse in here, beside
 * `SectionMenu` — one panel for where you are, one for who you are.
 *
 * **From `desktop:` to 2xl they stay in here too** (periods A6d): beside the section row they left it about 530px
 * short at 1024 and 110px at 1440 with a long room name and Seminole's counts, so Settings fell off the end. From 2xl
 * up this is not rendered and the switchers are in the header again; nothing here is kept in step with them beyond the
 * two handlers.
 *
 * Three things worth not re-deriving:
 *
 *   - **The room is always reported, the role only when there is a choice.** The room name is
 *     hidden below `sm` in the header, so a phone otherwise never says which room is on screen —
 *     and with several rooms carrying similar names that is not recoverable from the content. A
 *     role is different: an account holding one role has nothing to be ambiguous about, so a
 *     single-option control there would be the furniture the desktop switcher already refuses to
 *     be.
 *   - **Both switchers route through the caller's `leave`**, exactly as the desktop ones do.
 *     Switching either is a way out of an editor, and a phone is not where that stops being true.
 *   - **Opening About does not close the panel.** The dialog is rendered by a component *inside*
 *     it, so closing would unmount the dialog with it. `RailPanel` sits at `z-40` and
 *     `AboutDialog` at `z-50`, which puts the dialog over the panel and leaves the panel there to
 *     come back to.
 *
 * Chalk, never signal orange. A room and a role in force are statements about you, not work
 * waiting on you — the same rule the desktop switchers follow.
 */

const select =
  'h-11 w-full border border-white/20 bg-transparent px-2.5 text-[13px] text-rail-100 outline-none focus:border-white/50';

export function HeaderMenu({
  room,
  rooms,
  roomId,
  onSelectRoom,
  roleChoices,
  onSelectRole,
  displayName,
  signOut,
}: {
  room: Room;
  rooms: readonly Room[];
  roomId: string | null;
  onSelectRoom: (id: string) => void;
  roleChoices: RoleChoice[];
  onSelectRole: (role: ActingRole) => void;
  displayName?: string | null;
  signOut?: (() => void) | null;
}) {
  const { actingRole } = useActingRole();

  return (
    <RailPanel
      icon={<MoreHorizontal className="size-5" aria-hidden />}
      label="Room, role and account"
      hideFrom="2xl:hidden"
      heading={
        // Mirrors the header's own rule rather than inventing one: under the dev bypass there is
        // no account to name, and saying so is more use than the placeholder display name the
        // local session carries.
        <>
          <span className={`block ${panelLabel}`}>
            {signOut && displayName ? 'Signed in as' : 'Session'}
          </span>
          <span className="mt-0.5 block truncate text-[13px] text-white">
            {signOut ? (displayName ?? 'Signed in') : 'Dev bypass'}
          </span>
        </>
      }
    >
      {(close) => {
        // Closed before the handler runs, because `leave` may raise the unsaved-work dialog and
        // the question it asks should not be read through a panel. A cancelled switch leaves the
        // selects reporting the stored value either way — they are controlled by it, not by what
        // was picked.
        const act = (go: () => void) => {
          close();
          go();
        };

        return (
          <>
            <div className="space-y-3 border-t border-white/10 px-4 py-3.5">
              <label className="block">
                <span className={`mb-1.5 block ${panelLabel}`}>Room</span>
                {rooms.length > 1 ? (
                  <select
                    value={roomId ?? ''}
                    onChange={(e) => act(() => onSelectRoom(e.target.value))}
                    className={select}
                  >
                    {rooms.map((r) => (
                      <option key={r.id} value={r.id} className="text-ink-900">
                        {r.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  // Reported rather than offered. One room is not a choice, but it is still the
                  // answer to "which room is this", which the header cannot give below `sm`.
                  <span className="block truncate text-[13px] text-white">{room.name}</span>
                )}
              </label>

              {roleChoices.length > 1 && actingRole ? (
                <label className="block">
                  <span className={`mb-1.5 block ${panelLabel}`}>Acting as</span>
                  <select
                    value={actingRole}
                    onChange={(e) => act(() => onSelectRole(e.target.value as ActingRole))}
                    className={select}
                  >
                    {roleChoices.map((choice) => (
                      <option key={choice.id} value={choice.id} className="text-ink-900">
                        {shortLabelFor(choice.id as ActingRole)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-white/10 px-4 py-3">
              {/* Unconditional here, unlike the header's copy: this panel *is* the phone's chrome,
                  so the build somebody is being asked to read out has nowhere else to be. */}
              <About className="text-rail-300 hover:text-white" />
              {signOut ? (
                <button
                  type="button"
                  onClick={() => act(signOut)}
                  className="border border-white/20 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em] text-rail-300 transition hover:border-white/50 hover:text-white"
                >
                  Sign out
                </button>
              ) : null}
            </div>
          </>
        );
      }}
    </RailPanel>
  );
}
